import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useBeeHoney } from '../../context/HoneyFarmContext';
import { formatHoney } from '../../helpers/formatters';
import HoneyCostPopup from './HoneyCostPopup';
import HoneyDContract from '../../helpers/HONEYDContract';
import { ethers } from 'ethers';
import config from '../../config';

// Honey costs for bee operations
const HONEY_COSTS = {
  FEED: 0.23,
  WORK: 7,
  REVIVE: 7,
  BURN_REWARD: 12, // Instant reward, not a cost
};

export const enum BeeStatus {
  WORKER = 'Worker',
  REGULAR = 'Active',
  DEAD = 'Dead'
}

// Selection modes for batch operations
type SelectionMode = 'feed' | 'work' | 'revive' | 'burn' | 'unwork' | 'claim' | null;

interface Bee {
  id: number;
  image: string;
  honey: number;
  staked: number;
  active: number;
  lastTimeFed: number;
  epochsLeft: number;
  lastRewardTime?: number;
  level?: number;
}

interface BeeHoneyProps {
  signer?: ethers.Signer;
  address?: string;
}

// Check if a bee is truly alive (not missed feeding deadline)
const isBeeAlive = (bee: Bee): boolean => {
  // Worker bees are always alive (no feeding required)
  if (bee.staked === 1) return true;
  
  // If marked dead by contract
  if (bee.active === 0) return false;
  
  // If never fed, still alive (hasn't died yet)
  if (bee.lastTimeFed === 0) return true;
  
  // Check if feeding deadline has passed
  const now = Math.floor(Date.now() / 1000);
  const lastReward = bee.lastRewardTime || bee.lastTimeFed;
  const deathTime = lastReward + (bee.epochsLeft * 86400); // epochsLeft days in seconds
  
  return now < deathTime;
};

// Get time until death or time since death
const getTimeStatus = (bee: Bee): string => {
  if (bee.staked === 1) return 'Worker Bee - No feeding required';
  if (bee.active === 0) return 'Dead - Revive to reactivate';
  
  if (bee.lastTimeFed === 0) return 'Not fed since mint';
  
  const now = Math.floor(Date.now() / 1000);
  const lastReward = bee.lastRewardTime || bee.lastTimeFed;
  const deathTime = lastReward + (bee.epochsLeft * 86400);
  
  const diff = deathTime - now;
  
  if (diff > 0) {
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `Feed or work in ${days}d ${hours % 24}h`;
    }
    return `Feed or work in ${hours}h ${minutes}m`;
  } else {
    const absDiff = Math.abs(diff);
    const hours = Math.floor(absDiff / 3600);
    const minutes = Math.floor((absDiff % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `Missed feeding by ${days}d ${hours % 24}h`;
    }
    return `Missed feeding by ${hours}h ${minutes}m`;
  }
};

interface BeeRowProps {
  bee: Bee;
  selected: boolean;
  onSelect: (id: number, honeyCost: number) => void;
  selectionMode: SelectionMode;
  honeyBalance: number;
}

const BeeStatusBadge: React.FC<{ status: BeeStatus }> = ({ status }) => {
  let color;
  switch (status) {
    case BeeStatus.WORKER:
      color = 'bg-black bg-opacity-50 border-blue-400 text-blue-300';
      break;
    case BeeStatus.REGULAR:
      color = 'bg-black bg-opacity-50 border-green-400 text-green-300';
      break;
    case BeeStatus.DEAD:
      color = 'bg-black bg-opacity-50 border-red-400 text-red-300';
      break;
    default:
      color = 'bg-black bg-opacity-50 border-gray-500 text-gray-300';
  }
  return <span className={`px-2 py-1 rounded-md text-xs font-medium border ${color}`}>{status}</span>;
};

const BeeRow: React.FC<BeeRowProps> = ({ bee, selected, onSelect, selectionMode, honeyBalance }) => {
  const isWorker = bee.staked === 1;
  const isMarkedActive = bee.active === 1;
  const isAlive = isBeeAlive(bee);
  
  const status = isWorker ? BeeStatus.WORKER : 
                (isMarkedActive && isAlive ? BeeStatus.REGULAR : BeeStatus.DEAD);
  
  const timeStatus = getTimeStatus(bee);
  
  // Determine if this bee can be selected for the current action
  const canSelect = useMemo(() => {
    if (!selectionMode) return false;
    
    switch (selectionMode) {
      case 'feed':
        // Can only feed active, non-worker, alive bees
        return !isWorker && isMarkedActive && isAlive && honeyBalance >= HONEY_COSTS.FEED;
      case 'work':
        // Can only make non-worker, alive bees work
        return !isWorker && isMarkedActive && isAlive && honeyBalance >= HONEY_COSTS.WORK;
      case 'revive':
        // Can revive bees that are marked dead OR have missed feeding deadline
        return (!isMarkedActive || (!isWorker && !isAlive)) && honeyBalance >= HONEY_COSTS.REVIVE;
      case 'burn':
        // Can burn any bee
        return true;
      case 'unwork':
        // Can only stop workers
        return isWorker;
      case 'claim':
        // Can claim from any bee
        return true;
      default:
        return false;
    }
  }, [selectionMode, isWorker, isMarkedActive, isAlive, honeyBalance]);

  const getHoneyCostForAction = (): number => {
    switch (selectionMode) {
      case 'feed': return HONEY_COSTS.FEED;
      case 'work': return HONEY_COSTS.WORK;
      case 'revive': return HONEY_COSTS.REVIVE;
      case 'claim': return bee.honey;
      default: return 0;
    }
  };

  return (
    <div className={`flex items-center justify-between p-4 mb-4 bg-black bg-opacity-60 text-white shadow-md rounded-md border ${
      selected ? 'border-[#ffa2f8]' : 'border-[#55bac3]'
    } ${!canSelect && selectionMode ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-4">
        <img 
          src={bee.image} 
          alt={`Bee #${bee.id}`} 
          className="w-16 h-16 rounded-md shadow-md border border-[#c4f6f9]" 
        />
        <div>
          <div className="font-bold text-lg text-[#c4f6f9] mb-1">#{bee.id}</div>
          <div className="flex items-center gap-2 mb-1">
            <BeeStatusBadge status={status} />
            {bee.level !== undefined && bee.level > 0 && (
              <span className="px-2 py-1 rounded-md text-xs font-medium bg-black bg-opacity-50 border border-[#fcd34d] text-[#fcd34d]">
                LVL {bee.level}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">{timeStatus}</div>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <span className="font-mono text-sm text-[#fcd34d]">{formatHoney(bee.honey)} HONEY</span>
        {selectionMode && (
          <input 
            type="checkbox" 
            checked={selected}
            disabled={!canSelect}
            onChange={() => canSelect && onSelect(bee.id, getHoneyCostForAction())}
            className="w-6 h-6 rounded border-2 border-white checked:bg-[#ffa2f8] checked:border-[#ffa2f8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        )}
      </div>
    </div>
  );
};

const FILTERS = [BeeStatus.WORKER, BeeStatus.REGULAR, BeeStatus.DEAD];

const BeeHoney: React.FC<BeeHoneyProps> = ({ signer, address }) => {
  const { 
    bees, 
    honey: totalHoney,
    selected,
    loading,
    select,
    clearSelected,
    refreshStates,
    honeyFarmQueen
  } = useBeeHoney();

  const [filter, setFilter] = useState<BeeStatus | null>(null);
  const [searchId, setSearchId] = useState<string>('');
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(null);
  const [processing, setProcessing] = useState(false);
  const [honeyBalance, setHoneyBalance] = useState<number>(0);
  const [totalHoneyCost, setTotalHoneyCost] = useState<number>(0);

  // Read provider - bypasses MetaMask for read operations
  const readProvider = useMemo(() => {
    return new ethers.providers.JsonRpcProvider(config.infuraUrl);
  }, []);

  // Read contract (uses direct RPC to bypass MetaMask rate limits)
  const honeyReadContract = useMemo(() => {
    return new HoneyDContract(readProvider);
  }, [readProvider]);

  // Load honey balance
  const loadHoneyBalance = useCallback(async () => {
    if (!honeyReadContract || !address) return;
    
    try {
      const balance = await honeyReadContract.balanceOf(address);
      const formattedBalance = parseFloat(ethers.utils.formatEther(balance));
      setHoneyBalance(formattedBalance);
    } catch (error) {
      console.error('Error loading honey balance:', error);
      setHoneyBalance(0);
    }
  }, [honeyReadContract, address]);

  useEffect(() => {
    loadHoneyBalance();
  }, [loadHoneyBalance]);

  // Track honey cost when selecting bees
  const handleSelect = useCallback((beeId: number, honeyCost: number) => {
    const isCurrentlySelected = selected.includes(beeId);
    select(beeId);
    
    if (isCurrentlySelected) {
      setTotalHoneyCost(prev => Math.max(0, prev - honeyCost));
    } else {
      setTotalHoneyCost(prev => prev + honeyCost);
    }
  }, [selected, select]);

  // Cancel selection mode
  const cancelSelection = useCallback(() => {
    setSelectionMode(null);
    setTotalHoneyCost(0);
    clearSelected();
  }, [clearSelected]);

  // Start selection mode
  const startSelection = useCallback((mode: SelectionMode) => {
    setSelectionMode(mode);
    setTotalHoneyCost(0);
    clearSelected();
  }, [clearSelected]);

  // Execute action
  const executeAction = useCallback(async () => {
    if (!honeyFarmQueen || processing || selected.length === 0) return;
    
    setProcessing(true);
    try {
      let tx;
      switch (selectionMode) {
        case 'work':
          tx = await honeyFarmQueen.stakeBees(selected);
          break;
        case 'unwork':
          tx = await honeyFarmQueen.unstakeBees(selected);
          break;
        case 'feed':
          tx = await honeyFarmQueen.keepBeesActive(selected);
          break;
        case 'burn':
          tx = await honeyFarmQueen.burnBees(selected);
          break;
        case 'revive':
          tx = await honeyFarmQueen.activateBees(selected);
          break;
        case 'claim':
          tx = await honeyFarmQueen.claimBeesHoney(selected);
          break;
        default:
          return;
      }
      if (tx && tx.wait) await tx.wait();
      cancelSelection();
    } catch (error) {
      console.error(`Error performing ${selectionMode} action:`, error);
    }
    // Always reset processing state
    setProcessing(false);
    // Refresh in background after modal closes
    refreshStates().catch(console.error);
    loadHoneyBalance().catch(console.error);
  }, [honeyFarmQueen, processing, selected, selectionMode, refreshStates, loadHoneyBalance, cancelSelection]);

  // Claim all honey from all bees
  const handleClaimAll = useCallback(async () => {
    if (!honeyFarmQueen || processing || bees.length === 0) return;
    
    setProcessing(true);
    try {
      const beeIds = bees.map(bee => bee.id);
      const tx = await honeyFarmQueen.claimBeesHoney(beeIds);
      if (tx && tx.wait) await tx.wait();
    } catch (error) {
      console.error('Error claiming all bee honey:', error);
    }
    // Always reset processing state
    setProcessing(false);
    // Refresh in background after modal closes
    refreshStates().catch(console.error);
    loadHoneyBalance().catch(console.error);
  }, [honeyFarmQueen, processing, bees, refreshStates, loadHoneyBalance]);

  // Determine bee status for filtering
  const getBeeStatus = (bee: Bee): BeeStatus => {
    const isWorker = bee.staked === 1;
    const isAlive = isBeeAlive(bee);
    return isWorker ? BeeStatus.WORKER : (bee.active === 1 && isAlive ? BeeStatus.REGULAR : BeeStatus.DEAD);
  };

  // Filter bees based on current mode and filters
  const filteredBees = useMemo(() => {
    return bees.filter(bee => {
      const beeStatus = getBeeStatus(bee);
      
      // Apply status filter
      if (filter && beeStatus !== filter) return false;
      
      // Apply search filter
      if (searchId && bee.id.toString() !== searchId) return false;
      
      // When in selection mode, only show eligible bees
      if (selectionMode) {
        const isWorker = bee.staked === 1;
        const isMarkedActive = bee.active === 1;
        const isAlive = isBeeAlive(bee);
        
        switch (selectionMode) {
          case 'feed':
            return !isWorker && isMarkedActive && isAlive;
          case 'work':
            return !isWorker && isMarkedActive && isAlive;
          case 'revive':
            // Show bees that are either marked dead OR have missed feeding deadline
            return !isMarkedActive || (!isWorker && !isAlive);
          case 'unwork':
            return isWorker;
          case 'burn':
          case 'claim':
            return true;
        }
      }
      
      return true;
    });
  }, [bees, filter, searchId, selectionMode]);

  // Calculate totals for display
  const selectedCount = selected.length;
  const selectedBeeHoney = useMemo(() => {
    return bees.filter(b => selected.includes(b.id)).reduce((sum, b) => sum + b.honey, 0);
  }, [bees, selected]);

  // Check if action requires honey cost popup
  const needsHoneyPopup = selectionMode === 'feed' || selectionMode === 'work' || selectionMode === 'revive';
  const isClaimAction = selectionMode === 'claim';

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#c4f6f9]">
        <h1 className="text-xl font-bold text-[#c4f6f9]">My Bees</h1>
        <div className="text-sm text-gray-300 mt-2">
          Manage your bees - feed them to keep them alive, put them to work, or claim honey.
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span className="text-gray-400">
            Balance: <span className="text-[#fcd34d]">{honeyBalance.toFixed(2)} $HONEYD</span>
          </span>
          <span className="text-gray-400">
            Unclaimed: <span className="text-[#fcd34d]">{formatHoney(totalHoney)} HONEY</span>
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      {!selectionMode ? (
        <div className="mb-6 p-4 bg-black bg-opacity-60 rounded-md border border-[#55bac3]">
          <div className="text-[#c4f6f9] text-sm mb-4">
            Select an action to perform on multiple bees at once:
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => startSelection('feed')}
              className="px-4 py-2 bg-transparent border-2 border-pink-400 text-pink-400 rounded-md font-bold hover:bg-pink-400 hover:text-black transition-colors"
              title="Feed bees to keep them alive (0.23 HONEYD each)"
            >
              FEED BEES
            </button>
            <button
              onClick={() => startSelection('work')}
              className="px-4 py-2 bg-transparent border-2 border-[#55bac3] text-[#55bac3] rounded-md font-bold hover:bg-[#55bac3] hover:text-black transition-colors"
              title="Put bees to work - no feeding required but reduced honey (7 HONEYD each)"
            >
              WORK BEES
            </button>
            <button
              onClick={() => startSelection('unwork')}
              className="px-4 py-2 bg-transparent border-2 border-blue-400 text-blue-400 rounded-md font-bold hover:bg-blue-400 hover:text-black transition-colors"
              title="Stop worker bees from working"
            >
              STOP WORK
            </button>
            <button
              onClick={() => startSelection('revive')}
              className="px-4 py-2 bg-transparent border-2 border-purple-400 text-purple-400 rounded-md font-bold hover:bg-purple-400 hover:text-black transition-colors"
              title="Revive dead bees (7 HONEYD each)"
            >
              REVIVE BEES
            </button>
            <button
              onClick={() => startSelection('burn')}
              className="px-4 py-2 bg-transparent border-2 border-red-400 text-red-400 rounded-md font-bold hover:bg-red-400 hover:text-black transition-colors"
              title="Burn bees for instant 12 HONEYD each (PERMANENT)"
            >
              BURN BEES
            </button>
            <button
              onClick={() => startSelection('claim')}
              className="px-4 py-2 bg-transparent border-2 border-yellow-400 text-yellow-400 rounded-md font-bold hover:bg-yellow-400 hover:text-black transition-colors"
              title="Claim honey from selected bees"
            >
              CLAIM HONEY
            </button>
            <HoneyCostPopup
              requiredHoney={totalHoney}
              addressHoney={honeyBalance}
              callback={handleClaimAll}
              callBackArgs={[]}
              claim={true}
              trigger={
                <button
                  disabled={processing || bees.length === 0}
                  className="px-4 py-2 bg-transparent border-2 border-[#55bac3] text-[#55bac3] rounded-md font-bold hover:bg-[#55bac3] hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Claim all honey from all bees"
                >
                  {processing ? 'CLAIMING...' : 'CLAIM ALL'}
                </button>
              }
            />
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-black bg-opacity-60 rounded-md border border-[#ffa2f8]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-[#ffa2f8] font-bold mb-1">
                {selectionMode.toUpperCase()} MODE
              </div>
              <div className="text-sm text-gray-300">
                Selected: {selectedCount} bee{selectedCount !== 1 ? 's' : ''}
                {needsHoneyPopup && ` | Cost: ${totalHoneyCost.toFixed(2)} HONEYD`}
                {isClaimAction && ` | Claiming: ${formatHoney(selectedBeeHoney)} HONEY`}
                {selectionMode === 'burn' && ` | Reward: ${selectedCount * HONEY_COSTS.BURN_REWARD} HONEYD`}
              </div>
            </div>
            <div className="flex gap-3">
              {needsHoneyPopup ? (
                <HoneyCostPopup
                  requiredHoney={totalHoneyCost}
                  addressHoney={honeyBalance}
                  callback={executeAction}
                  callBackArgs={[]}
                  trigger={
                    <button
                      disabled={selectedCount === 0 || processing}
                      className={`px-6 py-2 rounded-md font-bold transition-colors ${
                        selectedCount > 0 && !processing
                          ? 'bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3] hover:text-black'
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed border-2 border-gray-600'
                      }`}
                    >
                      CONFIRM
                    </button>
                  }
                />
              ) : isClaimAction ? (
                <HoneyCostPopup
                  requiredHoney={selectedBeeHoney}
                  addressHoney={honeyBalance}
                  callback={executeAction}
                  callBackArgs={[]}
                  claim={true}
                  trigger={
                    <button
                      disabled={selectedCount === 0 || processing}
                      className={`px-6 py-2 rounded-md font-bold transition-colors ${
                        selectedCount > 0 && !processing
                          ? 'bg-transparent border-2 border-[#fcd34d] text-[#fcd34d] hover:bg-[#fcd34d] hover:text-black'
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed border-2 border-gray-600'
                      }`}
                    >
                      CONFIRM
                    </button>
                  }
                />
              ) : (
                <button
                  onClick={executeAction}
                  disabled={selectedCount === 0 || processing}
                  className={`px-6 py-2 rounded-md font-bold transition-colors ${
                    selectedCount > 0 && !processing
                      ? 'bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3] hover:text-black'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed border-2 border-gray-600'
                  }`}
                >
                  CONFIRM
                </button>
              )}
              <button
                onClick={() => clearSelected()}
                className="px-6 py-2 bg-transparent border-2 border-[#c4f6f9] text-[#c4f6f9] rounded-md font-bold hover:bg-[#c4f6f9] hover:text-black transition-colors"
              >
                CLEAR
              </button>
              <button
                onClick={cancelSelection}
                className="px-6 py-2 bg-transparent border-2 border-gray-500 text-gray-400 rounded-md font-bold hover:bg-gray-500 hover:text-white transition-colors"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex gap-2">
          {FILTERS.map(status => (
            <button
              key={status}
              onClick={() => setFilter(filter === status ? null : status)}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                filter === status 
                  ? 'bg-[#ffa2f8] text-black' 
                  : 'bg-black bg-opacity-60 text-white border border-[#55bac3] hover:border-[#ffa2f8]'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={searchId}
          onChange={e => setSearchId(e.target.value)}
          placeholder="Search by ID"
          className="px-4 py-2 bg-black bg-opacity-60 text-white rounded-md border border-[#55bac3] focus:border-[#c4f6f9] focus:outline-none"
        />
      </div>

      {/* Honey Cost Reference */}
      <div className="mb-6 p-3 bg-black bg-opacity-40 rounded-md border border-gray-700 text-xs text-gray-400">
        <span className="text-gray-300">Costs:</span>
        <span className="ml-3">Feed: {HONEY_COSTS.FEED}</span>
        <span className="ml-3">Work: {HONEY_COSTS.WORK}</span>
        <span className="ml-3">Revive: {HONEY_COSTS.REVIVE}</span>
        <span className="ml-3 text-green-400">Burn Reward: +{HONEY_COSTS.BURN_REWARD}</span>
      </div>

      {/* Bees List */}
      {loading ? (
        <div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
          <div className="animate-pulse text-[#c4f6f9]">Loading bees...</div>
        </div>
      ) : filteredBees.length === 0 ? (
        <div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
          <div className="text-gray-400">
            {bees.length === 0 ? "You don't own any bees." : "No bees match the current filter."}
          </div>
        </div>
      ) : (
        <div>
          {filteredBees.map(bee => (
            <BeeRow
              key={bee.id}
              bee={bee}
              selected={selected.includes(bee.id)}
              onSelect={handleSelect}
              selectionMode={selectionMode}
              honeyBalance={honeyBalance}
            />
          ))}
        </div>
      )}

      {/* Processing Overlay */}
      {processing && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="bg-black bg-opacity-90 border-2 border-[#ffa2f8] p-8 rounded-lg text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffa2f8] mx-auto mb-4"></div>
            <div className="text-[#ffa2f8]">Processing transaction...</div>
            {selectionMode && <div className="text-gray-400 text-sm mt-2">{selectionMode}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default BeeHoney;